'use client';
import HeaderButtons from '@components/shared/Header/components/HeaderButtons';
import { GridContainerColClasses, GridContainerGapClasses } from '@constants/layout';
import { ContentType } from '@lib/sanity/queries/modules/content/types';
import PortableText, { TextVariant } from '@ui/Text/PortableText';
import Unicase from '@ui/Unicase/Unicase';
import cn from '@utils/cn';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useIsInView from '../../../../lib/hooks/useIsInView';
import AccordionItem from './item';

type ContentAccordionClientProps = {
  icons: Record<string, React.ReactNode>;
  visuals: Record<string, React.ReactNode>;
  data: ContentType;
};

export default function ContentAccordionClient({ visuals, icons, data }: ContentAccordionClientProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isInitialSlide, setIsInitialSlide] = useState(true);
  const intervalNum = useRef<NodeJS.Timeout | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(containerRef);

  const getInterval = useCallback(() => {
    const selectedContent = data.contentList?.[selectedIndex];
    if (selectedContent?.showAnimation) {
      const length = data.contentList?.length ?? 0;
      intervalNum.current = setInterval(
        () => {
          if (selectedIndex === length - 1) {
            setSelectedIndex(0);
            setIsInitialSlide(false);
          } else {
            setSelectedIndex(selectedIndex + 1);
            setIsInitialSlide(false);
          }
        },
        (selectedContent.animationDuration ?? 5) * 1000,
      );
    }
  }, [data.contentList, selectedIndex]);

  useEffect(() => {
    if (isInView && !intervalNum.current) {
      getInterval();
    }
    return () => {
      if (intervalNum.current) {
        clearInterval(intervalNum.current);
        intervalNum.current = undefined;
      }
    };
  }, [isInView, getInterval]);

  function onClick(index: number) {
    setSelectedIndex(index);
    setIsInitialSlide(false);
    if (intervalNum.current) {
      clearInterval(intervalNum.current);
    }
    getInterval();
  }

  const selectedVisual = data.contentList?.[selectedIndex]?.visuals;

  return (
    <div ref={containerRef} className={cn(GridContainerColClasses, GridContainerGapClasses)}>
      <div
        className={cn(
          'flex h-full flex-col items-center justify-center',
          'col-span-4 gap-8 sm:col-span-6 md:col-span-4 lg:col-span-5 lg:gap-4',
          { 'md:order-2': data.align === 'right' },
        )}
      >
        <div className="flex w-full flex-col gap-2">
          <Unicase content={data.unicase?.value} isPill={data.unicaseIsPill} />
          <PortableText content={data.title} variant={TextVariant.TitleMD} />
        </div>

        <div className="flex w-full flex-col">
          {data.contentList?.map((item, index) => (
            <AccordionItem
              key={`${item.title?.value}_${index}`}
              item={item}
              isSelected={selectedIndex === index}
              isInitialSlide={isInitialSlide}
              onClick={onClick}
              index={index}
              icons={icons}
              showAnimation={isInView && item.showAnimation}
              animationDuration={item.animationDuration}
            />
          ))}
        </div>

        <HeaderButtons buttonPrimary={data.buttonPrimary} buttonSecondary={data.buttonSecondary} />
      </div>
      <div
        className={cn(
          'relative content-center',
          'min-h-[360px] sm:min-h-[500px] md:min-h-[400px] lg:min-h-[418px] xl:min-h-[580px]',
          'col-span-4 sm:col-span-6 md:col-span-4 lg:col-span-7',
        )}
      >
        {data.contentList?.map((content) => (
          <div
            key={content.visuals?._id}
            className={cn(
              'absolute inset-0 flex items-center justify-center transition-opacity duration-500',
              'h-[360px] sm:h-[500px] md:h-[400px] lg:h-[418px] xl:h-[580px]',
              {
                'opacity-0': selectedVisual?._id !== content.visuals?._id,
                'opacity-100': selectedVisual?._id === content.visuals?._id,
                'md:order-1': data.align === 'right',
                'lg:pl-8': data.align === 'left',
                'lg:pr-8': data.align === 'right',
              },
            )}
          >
            {content.visuals?._id && visuals[content.visuals?._id]}
          </div>
        ))}
      </div>
    </div>
  );
}
